import { FC, useCallback, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { api } from 'services/api';
import { TFilters } from 'redux/types';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgAlert } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgList } from '../common/PgList';
import { PgPage } from '../common/PgPage';
import { useReloadKey } from '../common/helpers';
import { DETAILS_BUTTON, pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { AlertDialog } from './AlertDialog';

type TRow = TPgAlert;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const ACKNOWLEDGE = 'Acknowledge';

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Last seen', 'lastSeenAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Severity', 'severity', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['critical', 'warning', 'info']),
	}),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(['open', 'acknowledged', 'resolved']),
	}),
	col('Type', 'type', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('Title', 'title', ETableStructureType.STRING, { tooltip: (item) => item.message ?? '' }),
	col('Count', 'occurrences', ETableStructureType.STRING, {
		sortable: true,
		value: (item) => String(item.occurrences),
	}),
	col('First seen', 'firstSeenAt', ETableStructureType.DATE_TIME, { sortable: true }),
	pgActionsColumn<TRow>([DETAILS_BUTTON], (item) =>
		item.availableActions.includes('acknowledge')
			? [
					DETAILS_BUTTON,
					{ name: ACKNOWLEDGE, label: 'Acknowledge', icon: EIconButtons.CHECK_CIRCLE },
				]
			: [DETAILS_BUTTON],
	),
];

/**
 * Alerts (03 §4.12): what needs an operator — failed payments, disputes, EAS conflicts, dead outbox
 * messages. Actions depend on the alert type; the card shows only those the gateway allows.
 */
export const Alerts: FC = () => {
	const [alertId, setAlertId] = useState<number | null>(null);
	const [onlyUnresolved, setOnlyUnresolved] = useState(true);
	const [reloadKey, reload] = useReloadKey();

	const extraFilters = useMemo<TFilters>(
		() =>
			onlyUnresolved
				? [{ fieldName: 'status', name: 'status', value: 'resolved', operator: '!=' }]
				: [],
		[onlyUnresolved],
	);

	const handleAction = useCallback(
		(row: TRow, action: string) => {
			if (action === DETAILS_BUTTON.name) {
				setAlertId(row.id);
				return;
			}
			if (action !== ACKNOWLEDGE) return;
			api.paymentGateway
				.acknowledgeAlert(row.id)
				.then(() => {
					notificationContainer('Alert acknowledged', 'success');
					reload();
				})
				.catch((e) =>
					notificationContainer(getPgErrorMessage(e, 'Failed to acknowledge'), 'error'),
				);
		},
		[reload],
	);

	return (
		<PgPage title="Alerts">
			<PgList<TRow>
				title="Alerts"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getAlerts}
				onAction={handleAction}
				extraFilters={extraFilters}
				defaultOrder={{ fieldName: 'lastSeenAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey}
				errorText="Failed to load alerts"
				headerContent={
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={onlyUnresolved}
								onChange={(e) => setOnlyUnresolved(e.target.checked)}
							/>
						}
						label="Only unresolved"
					/>
				}
			/>
			<AlertDialog alertId={alertId} onClose={() => setAlertId(null)} onChanged={reload} />
		</PgPage>
	);
};
